/* ============================================================
   The Hue Ghost page's hero demo: the living room's entertainment area
   while a film plays on the Apple TV, run the way Hue Ghost runs it.

   Nothing moves until the visitor presses play, as on the couch. Then the
   TV plays the film; Hue Ghost sees the Jellyfin session and opens the
   ghost, a muted twin of the film on a display nobody watches ("Ghost
   playing"); Hue Sync starts capturing it and the lamps follow the picture
   ("Syncing"). Stop, and the lights go off 1.5 s later while the ghost
   waits 10 s on standby in case the next episode starts; play again inside
   that window and it picks up where it was, with no relaunch.

   The panel is the app's Home page: its state card and wording, the now
   playing card, the drift readout and the Hue Sync line (hueghost/gui/
   pages.py), in the app's own colours. The TV plays a real montage cut from
   Sintel (Blender Foundation, CC BY 3.0) by default, or the drawn film.
   ============================================================ */

const DEVICE = 'Apple TV';
const AREA = 'Lounge room';
const TITLE = 'Sintel';

/* the app's states, colours and words (theme.py STATE_COLORS, pages.py) */
const STATES = {
  idle: { col: [107, 102, 93], title: 'Idle', sub: 'Waiting for ' + DEVICE + ' to play something.', tag: 'YOUR HUE SCENE' },
  ghosting: { col: [96, 165, 250], title: 'Ghost playing', sub: 'Waiting for Hue Sync to start streaming...', tag: 'HUE GHOST · OPENING THE GHOST' },
  syncing: { col: [61, 220, 151], title: 'Syncing', sub: 'Hue Sync is streaming the ghost to ' + AREA + '.', tag: 'HUE SYNC · STREAMING THE GHOST' },
  standby: { col: [167, 139, 250], title: 'Lights off, on standby', sub: '', tag: 'LIGHTS OFF · GHOST ON STANDBY' }
};
const ICONS = {
  idle: '<svg viewBox="0 0 24 24"><path d="M20.5 14.2A8.5 8.5 0 0 1 9.8 3.5 8.5 8.5 0 1 0 20.5 14.2Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>',
  ghosting: '<svg viewBox="0 0 24 24"><path d="M8 5.5v13l10.5-6.5L8 5.5Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>',
  syncing: '<svg viewBox="0 0 24 24"><path d="M9 21h6M10 18h4M12 3a6 6 0 0 0-3.5 10.9V16h7v-2.1A6 6 0 0 0 12 3Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  standby: '<svg viewBox="0 0 24 24"><path d="M9 6v12M15 6v12" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>'
};
const PLAY_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7L8 5Z" fill="currentColor"/></svg>';
const PAUSE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor"/></svg>';

/* the room's own scene while Hue Sync is not streaming: warm white, low */
const SCENE = [[255, 186, 122, 0.34], [255, 186, 122, 0.3], [255, 196, 140, 0.5], [255, 186, 122, 0.3], [255, 180, 112, 0.36]];

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const rgb = (c, k = 1, a = 1) => 'rgba(' + Math.round(c[0] * k) + ',' + Math.round(c[1] * k) + ',' + Math.round(c[2] * k) + ',' + a + ')';
const mixc = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const mmss = (s) => { s = Math.max(0, Math.floor(s || 0)); return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };

const INSTANCES = new WeakMap();

class GhostDemo {
  constructor(root) {
    this.root = root;
    this.refs = {};
    this.listeners = [];
    this.phase = 'idle';
    this.tvOn = false;           /* the TV is playing the film */
    this.tvPaused = false;
    this.ghostOpen = false;
    this.tvT = 0;                /* seconds into the film (drawn mode's own clock) */
    this.drift = null; this.speed = 1; this.nudges = 0; this.seeks = 0;
    this.timer = { sync: 0, lightsOff: 0, standby: 0 };
    this.lamps = SCENE.map((s) => s.slice(0, 3));
    this.lampK = SCENE.map((s) => s[3]);
    this.filmMode = 'real';
    this.reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  on(t, ty, fn, o) { t.addEventListener(ty, fn, o); this.listeners.push([t, ty, fn, o]); }

  mount() {
    this.root.querySelectorAll('[data-ref]').forEach((el) => { this.refs[el.getAttribute('data-ref')] = el; });
    this.lampDots = this.refs.lampRow ? Array.from(this.refs.lampRow.querySelectorAll('i')) : [];
    let cfg = {};
    try { cfg = JSON.parse(this.root.querySelector('[data-osshub-config]').textContent) || {}; } catch (e) { /* no config */ }
    this.cfg = cfg;
    if (!cfg.film) this.filmMode = 'drawn';
    this.on(this.root, 'click', (e) => {
      const el = e.target.closest('[data-act]');
      if (!el || !this.root.contains(el) || el.disabled) return;
      const act = el.getAttribute('data-act');
      if (act === 'play') this.pressPlay();
      if (act === 'stop') this.pressStop();
      if (act === 'film') this.pickFilm(el.getAttribute('data-film'));
    });
    this.markFilm();
    this.render(true);
    /* on a phone the room stays pinned under the site's menu bar while the
       panel scrolls beneath it */
    const pin = () => {
      const nav = document.querySelector('.cyborg-nav');
      this.root.style.setProperty('--osl-pin', (nav ? Math.round(nav.getBoundingClientRect().height) : 0) + 'px');
    };
    pin();
    this.on(window, 'resize', pin);

    /* nothing is drawn or decoded while the room is out of view */
    this.visible = true;
    if ('IntersectionObserver' in window && this.refs.stage) {
      this.io = new IntersectionObserver((ents) => { this.visible = ents[0].isIntersecting; }, { rootMargin: '120px 0px' });
      this.io.observe(this.refs.stage);
    }

    const spec = cfg.room || '';
    import(/^(https?:)?\/\//.test(spec) ? spec : new URL(spec, document.baseURI).href).then((mod) => {
      this.M = mod;
      this.room = new mod.Room(this.refs.stage, { pendant: true });
      if (cfg.film && mod.createVideoFilm) {
        const abs = (u) => (/^(https?:)?\/\//.test(u) ? u : new URL(u, document.baseURI).href);
        this.video = mod.createVideoFilm(abs(cfg.film), { poster: cfg.poster ? abs(cfg.poster) : null });
      }
      this.on(window, 'resize', () => { this.room.resize(); this.draw(0); });
      this.last = performance.now();
      const loop = (now) => {
        const dt = Math.min(0.05, (now - this.last) / 1000);
        this.last = now;
        this.step(dt);
        if (this.visible && !document.hidden) this.draw(dt);
        this.driveVideo();
        this.raf = requestAnimationFrame(loop);
      };
      this.raf = requestAnimationFrame(loop);
      this.draw(0);
    }).catch((e) => console.error('[hue-ghost] room failed to load', e));
  }

  destroy() {
    this.listeners.forEach(([t, ty, fn, o]) => t.removeEventListener(ty, fn, o));
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.io) this.io.disconnect();
    if (this.video) this.video.pause();
  }

  real() { return this.filmMode === 'real' && this.video && !this.video.failed; }
  duration() {
    if (this.real()) return this.video.el.duration || 15.8;
    return (this.M && this.M.FILM_TOTAL) || 16;
  }
  position() { return this.real() ? this.video.el.currentTime || 0 : this.tvT; }

  /* ---------- what the visitor does, as on the couch ---------- */

  pressPlay() {
    if (this.tvOn) {
      /* pause and resume: the ghost follows the TV, and the lights hold the
         paused picture */
      this.tvPaused = !this.tvPaused;
      this.render(true);
      return;
    }
    this.tvOn = true;
    this.tvPaused = false;
    this.timer.lightsOff = 0;
    const reuse = this.ghostOpen;         /* still parked on standby */
    this.ghostOpen = true;
    this.phase = 'ghosting';
    /* the ghost renders first, then Hue Sync starts on it */
    this.timer.sync = reuse ? 0.35 : 1.3;
    this.timer.standby = 0;
    if (!reuse) { this.drift = 0.32 + Math.random() * 0.1; this.nudges = 0; this.seeks = 0; }
    if (this.real()) { try { if (!reuse) this.video.el.currentTime = 0; } catch (e) { /* not seekable yet */ } }
    else if (!reuse) this.tvT = 0;
    this.render(true);
  }

  pressStop() {
    if (!this.tvOn) return;
    this.tvOn = false;
    this.tvPaused = false;
    /* lights off 1.5 s after the TV stops; the ghost stays parked 10 s */
    this.timer.lightsOff = 1.5;
    this.timer.sync = 0;
    this.render(true);
  }

  pickFilm(mode) {
    if (mode === this.filmMode || (mode === 'real' && !this.cfg.film)) return;
    this.filmMode = mode;
    this.markFilm();
  }

  markFilm() {
    const host = this.refs.filmPick;
    if (host) host.querySelectorAll('[data-film]').forEach((b) => {
      const on = b.getAttribute('data-film') === this.filmMode;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-checked', String(on));
    });
    if (this.refs.credit) this.refs.credit.classList.toggle('is-real', this.filmMode === 'real');
  }

  /* the real film runs only while the TV plays it and the room is in view */
  driveVideo() {
    const v = this.video;
    if (!v) return;
    const want = this.real() && this.tvOn && !this.tvPaused && this.visible && !document.hidden;
    if (want !== !!this._vidOn) { this._vidOn = want; if (want) v.play(); else v.pause(); }
  }

  /* ---------- the model ---------- */

  step(dt) {
    if (this.tvOn && !this.tvPaused && !this.real()) this.tvT = (this.tvT + dt) % this.duration();
    if (this.timer.sync > 0) {
      this.timer.sync -= dt;
      if (this.timer.sync <= 0 && this.tvOn) { this.phase = 'syncing'; this.render(true); }
    }
    if (this.timer.lightsOff > 0) {
      this.timer.lightsOff -= dt;
      if (this.timer.lightsOff <= 0) { this.phase = 'standby'; this.timer.standby = 10; this.render(true); }
    }
    if (this.phase === 'standby' && this.timer.standby > 0) {
      const before = Math.ceil(this.timer.standby);
      this.timer.standby -= dt;
      if (this.timer.standby <= 0) { this.phase = 'idle'; this.ghostOpen = false; this.drift = null; this.render(true); }
      else if (Math.ceil(this.timer.standby) !== before) this.render(false);
    }
    /* the ghost settles onto the TV: small speed nudges pull the drift in */
    if (this.ghostOpen && this.drift != null && this.tvOn && !this.tvPaused) {
      const target = 0.02 * Math.sin(performance.now() / 2300);
      const before = this.drift;
      this.drift += (target - this.drift) * (1 - Math.exp(-dt / 1.1));
      this.speed = 1 + clamp((before - this.drift) / Math.max(dt, 1e-3) * 0.02, -0.04, 0.04);
      if (Math.abs(before) > 0.05 && Math.abs(this.drift) <= 0.05) this.nudges++;
      if (Math.random() < dt * 0.6 && Math.abs(this.drift) > 0.04) this.nudges++;
    } else this.speed = 1;
    if (this.tvOn || this.timer.lightsOff > 0) this.render(false);
  }

  /* ---------- the panel: the app's Home page ---------- */

  render(full) {
    const R = this.refs, S = STATES[this.phase];
    const now = performance.now();
    if (!full && this._rt && now - this._rt < 250) return;
    this._rt = now;
    if (R.hero) {
      if (full) {
        R.hero.setAttribute('data-state', this.phase);
        R.hero.style.background = this.phase === 'syncing'
          ? 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 45%, #1c1a16 100%)'
          : 'linear-gradient(135deg, ' + rgb(S.col, 1, 0.9) + ' 0%, #1c1a16 100%)';
        if (R.heroIc) R.heroIc.innerHTML = ICONS[this.phase];
        if (R.heroTxt) R.heroTxt.textContent = S.title;
      }
      if (R.heroSub) {
        R.heroSub.textContent = this.phase === 'standby'
          ? 'The TV stopped; the ghost waits ' + Math.max(1, Math.ceil(this.timer.standby)) + ' s in case it comes straight back.'
          : this.timer.lightsOff > 0 ? 'The TV stopped. Lights off in ' + this.timer.lightsOff.toFixed(1) + ' s.' : S.sub;
      }
    }
    const playing = this.tvOn;
    if (R.npTitle) R.npTitle.textContent = playing ? TITLE : 'Ready';
    if (R.npDev) R.npDev.innerHTML = DEVICE + (playing ? ' <em>' + (this.tvPaused ? 'paused' : '→ ' + AREA) + '</em>' : '');
    const d = this.duration(), p = playing ? this.position() : 0;
    if (R.npBar) R.npBar.style.width = playing ? clamp(p / d * 100, 0, 100).toFixed(1) + '%' : '0%';
    if (R.npTime) R.npTime.textContent = playing ? mmss(p) + '  /  ' + mmss(d) : '';
    if (R.poster) R.poster.classList.toggle('is-on', playing);
    if (R.drift) {
      /* Time lock is on by default: once the ghost matches the TV the app shows it locked */
      const settled = this.ghostOpen && this.drift != null && this.tvOn && !this.tvPaused && Math.abs(this.drift) < 0.03;
      R.drift.textContent = settled ? 'drift +0.00 s · locked'
        : this.ghostOpen && this.drift != null ? 'drift ' + (this.drift <= -0.005 ? '−' : '+') + Math.abs(this.drift).toFixed(2) + ' s' : 'drift: waiting';
    }
    if (R.chips) {
      R.chips.innerHTML = this.ghostOpen && this.drift != null
        ? '<em>ghost ' + mmss(p + this.drift) + '</em><em>speed ' + this.speed.toFixed(3) + '</em><em>seeks ' + this.seeks + '</em><em>nudges ' + this.nudges + '</em>'
        : '<em>ghost ready</em>';
    }
    if (full) {
      const sync = this.phase === 'syncing';
      if (R.hsPill) {
        R.hsPill.textContent = sync ? 'syncing' : 'connected';
        R.hsPill.setAttribute('data-hs', sync ? 'sync' : this.phase === 'ghosting' ? 'start' : 'idle');
      }
      if (R.playBtn) {
        R.playBtn.innerHTML = (playing && !this.tvPaused ? PAUSE_ICON + 'Pause' : PLAY_ICON + (playing ? 'Resume' : this.ghostOpen ? 'Play again' : 'Play on the ' + DEVICE));
        R.playBtn.classList.toggle('is-on', playing && !this.tvPaused);
      }
      if (R.stopBtn) R.stopBtn.disabled = !playing;
      if (R.cue) R.cue.classList.toggle('is-gone', playing || this.ghostOpen);
    }
  }

  /* ---------- the room ---------- */

  targets(film) {
    if (this.phase === 'syncing' && film) {
      /* Hue Sync in video mode: each lamp takes the edge of the picture it
         stands beside, the top of the frame for the lamps up high */
      return [[film.l, 0.92], [film.l, 0.96], [film.t, 0.6], [film.r, 0.96], [mixc(film.r, film.t, 0.4), 0.88]];
    }
    if (this.phase === 'standby') return SCENE.map((s) => [s.slice(0, 3), s[3] * 0.9]);
    return SCENE.map((s) => [s.slice(0, 3), s[3]]);
  }

  draw(dt) {
    if (!this.room) return;
    const M = this.M;
    const real = this.real();
    const tt = this.reduced ? 0 : performance.now() / 1000;
    const tvLive = this.tvOn;
    /* for the 1.5 s after a stop Hue Sync still holds the last picture */
    let film = tvLive ? (real ? this.video.sample() : M.filmAt(this.tvT)) : null;
    if (film) this.lastFilm = film;
    else if (this.phase === 'syncing' && this.timer.lightsOff > 0) film = this.lastFilm || null;
    const tg = this.targets(film);
    /* Hue Sync smooths its capture a little; the scene fades in slower */
    const kk = dt ? 1 - Math.pow(0.5, dt / (this.phase === 'syncing' ? 0.09 : 0.35)) : 1;
    for (let i = 0; i < 5; i++) {
      this.lamps[i] = mixc(this.lamps[i], tg[i][0], kk);
      this.lampK[i] += (tg[i][1] - this.lampK[i]) * kk;
    }
    const L = this.lamps.map((c, i) => c.map((v) => v * this.lampK[i]));
    this.paintLampRow(L);
    const S = STATES[this.phase];
    this.room.draw({
      lamps: { barL: L[0], stripL: L[1], pendant: L[2], stripR: L[3], barR: L[3], signe: L[4], stripT: mixc(L[1], L[3], 0.5) },
      tvLight: film ? mixc(film.l, film.r, 0.5) : [16, 18, 26],
      tv: (ctx, x, y, w, h) => this.paintTv(ctx, x, y, w, h, film, tt),
      overlay: (ctx, G, s) => {
        const label = S.tag;
        ctx.font = '600 ' + Math.round(11 * s) + 'px Inter, sans-serif';
        ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
        const tw = ctx.measureText(label).width, bx = 16 * s, by = 16 * s, bh = 28 * s;
        ctx.fillStyle = 'rgba(10,10,12,0.72)';
        ctx.beginPath(); ctx.roundRect ? ctx.roundRect(bx, by, tw + 38 * s, bh, bh / 2) : ctx.rect(bx, by, tw + 38 * s, bh); ctx.fill();
        ctx.fillStyle = rgb(S.col);
        ctx.beginPath(); ctx.arc(bx + 14 * s, by + bh / 2, 4 * s, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.88)';
        ctx.fillText(label, bx + 26 * s, by + bh / 2 + 0.5 * s);
      }
    });
  }

  /* the TV: the film while it plays; before that the title waiting, dimmed,
     the way a player sits on a film's page */
  paintTv(ctx, x, y, w, h, film, tt) {
    const M = this.M;
    if (this.real()) this.video.draw(ctx, x, y, w, h);
    else M.drawFilm(ctx, x, y, w, h, this.tvOn ? film || M.filmAt(this.tvT) : M.filmAt(0), this.tvOn ? this.tvT : 0);
    if (this.tvOn && !this.tvPaused) return;
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,' + (this.tvOn ? 0.3 : 0.55) + ')';
    ctx.fillRect(x, y, w, h);
    const r = Math.min(w, h) * 0.11, cx = x + w / 2, cy = y + h / 2;
    ctx.fillStyle = 'rgba(255,255,255,0.14)';
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    if (this.tvOn) { ctx.fillRect(cx - r * 0.34, cy - r * 0.4, r * 0.22, r * 0.8); ctx.fillRect(cx + r * 0.12, cy - r * 0.4, r * 0.22, r * 0.8); }
    else { ctx.beginPath(); ctx.moveTo(cx - r * 0.3, cy - r * 0.45); ctx.lineTo(cx + r * 0.5, cy); ctx.lineTo(cx - r * 0.3, cy + r * 0.45); ctx.closePath(); ctx.fill(); }
    if (!this.tvOn) {
      ctx.font = '600 ' + Math.max(9, Math.round(h * 0.075)) + 'px Inter, sans-serif';
      ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      ctx.fillText(TITLE, x + w * 0.06, y + h * 0.88);
    }
    ctx.restore();
  }

  /* the five lamps as dots, the way the app mirrors a room */
  paintLampRow(L) {
    if (!this.lampDots.length) return;
    const now = performance.now();
    if (this._rowAt && now - this._rowAt < 60) return;
    this._rowAt = now;
    this.lampDots.forEach((d, i) => {
      const c = L[i], m = Math.max(c[0], c[1], c[2], 1), k = Math.min(1, m / 255);
      const hue = c.map((v) => Math.round(v * 255 / m));
      d.style.background = 'rgba(' + hue.join(',') + ',' + (0.2 + 0.8 * k).toFixed(2) + ')';
      d.style.boxShadow = k > 0.05 ? '0 0 ' + Math.round(4 + 18 * k) + 'px rgba(' + hue.join(',') + ',' + (0.75 * k).toFixed(2) + ')' : 'none';
    });
  }
}

function boot(root) {
  if (!root || INSTANCES.has(root)) return;
  const inst = new GhostDemo(root);
  INSTANCES.set(root, inst);
  inst.mount();
}
function bootAll(scope) { (scope || document).querySelectorAll('[data-osshub]').forEach(boot); }

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => bootAll());
else bootAll();

document.addEventListener('shopify:section:load', (e) => bootAll(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('[data-osshub]').forEach((root) => {
    const inst = INSTANCES.get(root);
    if (inst) { inst.destroy(); INSTANCES.delete(root); }
  });
});
